import {toMoneyFromCents, type Money} from "../../../../../lib/Money.ts";
import type {CurrencyEnum, PresentationSourceType} from "../../../../../generated/enums.ts";
import type {PanelBooking} from "../types.ts";

export const NO_VAT_LABEL = "Geen btw";

export interface SortableVatRow {
    key: string;
    relationName: string | null;
    invoiceNumber: string | null;
    title: string;
    date: string | null;
    vatSortLabel: string | null;
    debit: Money;
    credit: Money;
}

export interface VatRow extends SortableVatRow {
    documentKey: string;
    kind: PresentationSourceType | null;
    billId: string | null;
    vatLabel: string;
    vatKnown: boolean;
    bookings: PanelBooking[];
}

export interface DocumentSummary extends SortableVatRow {
    kind: PresentationSourceType | null;
    billId: string | null;
    parts: VatRow[];
}

function earliestDate(dates: (string | null)[]): string | null {
    const known = dates.filter((date): date is string => date !== null).sort();
    return known[0] ?? null;
}

function sumCents(amounts: Money[]): number {
    return amounts.reduce((sum, amount) => sum + amount.signed_amount, 0);
}

function compareText(left: string | null, right: string | null): number {
    if (left === right) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    return left.localeCompare(right, "nl", {numeric: true, sensitivity: "base"});
}

function lowestVatLabel(labels: (string | null)[]): string | null {
    return [...labels].sort(compareText)[0] ?? null;
}

function rowKeyFor(booking: PanelBooking, label: string): {key: string; documentKey: string} {
    if (booking.kind === "BILL" && booking.billId) {
        return {key: `${booking.billId}|${label}`, documentKey: booking.billId};
    }
    return {key: booking.key, documentKey: booking.key};
}

export function buildVatRows(bookings: PanelBooking[], currency: CurrencyEnum): VatRow[] {
    const order: string[] = [];
    const membersByKey = new Map<string, {documentKey: string; label: string; bookings: PanelBooking[]}>();
    for (const booking of bookings) {
        const label = booking.vatLabel ?? NO_VAT_LABEL;
        const {key, documentKey} = rowKeyFor(booking, label);
        const existing = membersByKey.get(key);
        if (existing) {
            existing.bookings.push(booking);
        } else {
            membersByKey.set(key, {documentKey, label, bookings: [booking]});
            order.push(key);
        }
    }

    return order.map((key) => {
        const {documentKey, label, bookings: members} = membersByKey.get(key)!;
        const first = members[0];
        return {
            key,
            documentKey,
            kind: first.kind,
            billId: first.billId,
            relationName: first.relationName,
            invoiceNumber: first.invoiceNumber,
            title: first.title,
            date: earliestDate(members.map((member) => member.date)),
            vatLabel: label,
            vatSortLabel: first.vatLabel,
            vatKnown: first.vatLabel !== null,
            debit: toMoneyFromCents(sumCents(members.map((member) => member.debit)), currency),
            credit: toMoneyFromCents(sumCents(members.map((member) => member.credit)), currency),
            bookings: members,
        };
    });
}

export type VatRowSortColumn = "relation" | "invoice" | "date" | "vat" | "debit" | "credit";

export interface VatRowSort {
    column: VatRowSortColumn;
    ascending: boolean;
}

function dateValue(date: string | null): number {
    if (!date) return Number.MAX_SAFE_INTEGER;
    const timestamp = new Date(date).getTime();
    return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
}

function compareBy(left: SortableVatRow, right: SortableVatRow, column: VatRowSortColumn): number {
    switch (column) {
        case "relation":
            return compareText(left.relationName ?? left.title, right.relationName ?? right.title);
        case "invoice":
            return compareText(left.invoiceNumber, right.invoiceNumber);
        case "date":
            return dateValue(left.date) - dateValue(right.date);
        case "vat":
            return compareText(left.vatSortLabel, right.vatSortLabel);
        case "debit":
            return left.debit.signed_amount - right.debit.signed_amount;
        case "credit":
            return left.credit.signed_amount - right.credit.signed_amount;
        default: {
            const _exhaustive: never = column;
            return _exhaustive;
        }
    }
}

export function sortVatRows<Row extends SortableVatRow>(rows: Row[], sort: VatRowSort): Row[] {
    const direction = sort.ascending ? 1 : -1;
    return [...rows].sort(
        (left, right) =>
            compareBy(left, right, sort.column) * direction
            || dateValue(left.date) - dateValue(right.date)
            || compareText(left.key, right.key),
    );
}

export function sortByVatRate<Row extends {vatSortLabel: string | null}>(rows: Row[]): Row[] {
    return [...rows].sort((left, right) => compareText(left.vatSortLabel, right.vatSortLabel));
}

export function toggleVatRowSort(current: VatRowSort, column: VatRowSortColumn): VatRowSort {
    if (current.column === column) return {column, ascending: !current.ascending};
    return {column, ascending: true};
}

export function summarizeDocuments(rows: VatRow[], currency: CurrencyEnum): DocumentSummary[] {
    const order: string[] = [];
    const partsByDocument = new Map<string, VatRow[]>();
    for (const row of rows) {
        const existing = partsByDocument.get(row.documentKey);
        if (existing) {
            existing.push(row);
        } else {
            partsByDocument.set(row.documentKey, [row]);
            order.push(row.documentKey);
        }
    }
    return order.map((documentKey) => {
        const parts = sortByVatRate(partsByDocument.get(documentKey)!);
        const first = parts[0];
        return {
            key: documentKey,
            kind: first.kind,
            billId: first.billId,
            relationName: first.relationName,
            invoiceNumber: first.invoiceNumber,
            title: first.title,
            date: earliestDate(parts.map((part) => part.date)),
            vatSortLabel: lowestVatLabel(parts.map((part) => part.vatSortLabel)),
            debit: toMoneyFromCents(sumCents(parts.map((part) => part.debit)), currency),
            credit: toMoneyFromCents(sumCents(parts.map((part) => part.credit)), currency),
            parts,
        };
    });
}

export const NO_RELATION_LABEL = "Zonder relatie";

export interface RelationDocuments extends SortableVatRow {
    documents: DocumentSummary[];
}

function groupBy<Item>(items: Item[], keyOf: (item: Item) => string): Map<string, Item[]> {
    const groups = new Map<string, Item[]>();
    for (const item of items) {
        const key = keyOf(item);
        const existing = groups.get(key);
        if (existing) existing.push(item);
        else groups.set(key, [item]);
    }
    return groups;
}

const RELATIONS_A_TO_Z: VatRowSort = {column: "relation", ascending: true};

export function groupDocumentsByRelation(documents: DocumentSummary[], currency: CurrencyEnum): RelationDocuments[] {
    const byRelation = groupBy(documents, (document) => document.relationName ?? NO_RELATION_LABEL);
    const relations = [...byRelation.entries()].map(([relationName, members]) => ({
        key: relationName,
        relationName,
        invoiceNumber: null,
        title: relationName,
        date: earliestDate(members.map((document) => document.date)),
        vatSortLabel: lowestVatLabel(members.map((document) => document.vatSortLabel)),
        debit: toMoneyFromCents(sumCents(members.map((document) => document.debit)), currency),
        credit: toMoneyFromCents(sumCents(members.map((document) => document.credit)), currency),
        documents: members,
    }));
    return sortVatRows(relations, RELATIONS_A_TO_Z);
}
